// src/components/core/SchemaViewer.tsx
import React, { useEffect, useRef, useState } from "react";
import { useDatabase, useToast } from "@/lib";
import { useTableSchema } from "@/core/schema/useTableSchema";
import { classifyType, fmtValue, valueClass } from "@/core/schema/schemaFormat";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import {
  Layout,
  RefreshCw,
  Info,
  Copy as CopyIcon,
  Check as CheckIcon,
} from "lucide-react";

interface Props {
  initialTable?: string;
  setMsg: (m: string) => void;
}

// ── Spinner is used in the header only ─────────────────────────
const Spinner: React.FC = () => (
  <div className="inline-block h-3 w-3 border-2 border-muted-foreground/30 border-t-foreground animate-spin rounded-full ml-1" />
);

export default function SchemaViewer({ initialTable, setMsg }: Props) {
  const { conn, tables, refreshTables } = useDatabase();
  const [table, setTable] = useState<string>("");

  const {
    schemaData,
    columnStats,
    isLoading,
    metaReady,
    statsReady,
    error,
    reload,
  } = useTableSchema(conn, table || null);

  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const prevLoadingRef = useRef<boolean>(false);

  // Sync selection when tables list changes
  useEffect(() => {
    if (!tables.length) {
      setTable("");
      return;
    }
    if (tables.includes(table)) return;
    if (initialTable && tables.includes(initialTable)) {
      setTable(initialTable);
    } else {
      setTable(tables[0]);
    }
  }, [tables, initialTable, table]);

  // Toast messages
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    if (!wasLoading && isLoading) setMsg(`Loading schema for "${table}"…`);
    if (wasLoading && !isLoading) {
      if (error) setMsg(`Error loading schema: ${error.message}`);
      else if (metaReady) setMsg(`Schema for "${table}" loaded.`);
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, error, metaReady, table, setMsg]);

  // Simple one‐line copy text
  const simpleSchema = schemaData
    .map((c) => `${c.name || c.NAME}: ${c.type_formatted}`)
    .join("\n");

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layout className="h-5 w-5 text-brand" /> Schema
        </CardTitle>
        <CardDescription>
          View 5 random example values per column
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col space-y-6 overflow-hidden">
        {/* Always-enabled selector bar */}
        <SelectorBar
          tables={tables}
          table={table}
          setTable={setTable}
          isLoading={isLoading}
          onRefresh={() => {
            refreshTables();
            reload();
          }}
        />

        {/* Main body */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {!table ? (
            <EmptyState
              icon={Info}
              heading="No table selected"
              sub={
                tables.length
                  ? "Select a table above."
                  : "Register a table first."
              }
            />
          ) : isLoading && !metaReady ? (
            <EmptyState spinner heading="Loading schema…" sub="Please wait…" />
          ) : !schemaData.length ? (
            <EmptyState
              icon={Layout}
              heading="No schema available"
              sub="Table does not exist or failed to load."
            />
          ) : (
            <>
              {/* Copy button for basic schema */}
              <div className="flex justify-end text-sm mb-2">
                <CopyBtn
                  copied={copied}
                  setCopied={setCopied}
                  title="Schema copied"
                  text={simpleSchema}
                  disabled={!metaReady}
                />
              </div>

              {/* Schema table */}
              <div className="overflow-x-auto border rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b">
                      <th className="p-2 text-left font-medium">Column</th>
                      <th className="p-2 text-left font-medium">Type</th>
                      <th
                        className="p-2 text-left font-medium"
                        colSpan={5}
                      >
                        Example Values{" "}
                        {!statsReady && <Spinner />}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {schemaData.map((col, i) => {
                      const name = col.name || col.NAME;
                      const dtype = col.type_formatted;
                      const stats =
                        columnStats.find((s) => s.column_name === name) || {
                          null_count: 0,
                          top_values: [],
                        };
                      const cls = classifyType(dtype);

                      return (
                        <tr key={i} className="border-b hover:bg-muted/10">
                          <td className="p-2 font-mono font-medium">{name}</td>
                          <td className="p-2">
                            <code className="px-1.5 py-0.5 bg-muted/30 rounded text-xs">
                              {dtype}
                            </code>
                          </td>
                          {stats.top_values.map((tv, idx) => (
                            <td key={idx} className="p-2">
                              <span className={valueClass(tv.value, cls)}>
                                {fmtValue(tv.value, cls)}
                              </span>
                            </td>
                          ))}
                          {/* fill empty cells if fewer than 5 */}
                          {[...Array(5 - stats.top_values.length)].map(
                            (_, idx) => (
                              <td
                                key={`empty-${idx}`}
                                className="p-2 text-muted-foreground"
                              >
                                –
                              </td>
                            )
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ────────────── SelectorBar ────────────── */
function SelectorBar({
  tables,
  table,
  setTable,
  isLoading,
  onRefresh,
}: {
  tables: string[];
  table: string;
  setTable: (t: string) => void;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end gap-4">
      <div className="flex-1 space-y-1.5">
        <label htmlFor="table-select" className="text-sm font-medium">
          Select Table
        </label>
        <Select
          id="table-select"
          value={table}
          onValueChange={setTable}
          disabled={false /* always enabled */}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select a table" />
          </SelectTrigger>
          <SelectContent>
            {tables.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="h-10 gap-1.5 shrink-0"
        disabled={isLoading || !table}
        onClick={onRefresh}
      >
        {isLoading ? <Spinner /> : <RefreshCw className="h-4 w-4" />}
        <span>Refresh</span>
      </Button>
    </div>
  );
}

/* ────────────── EmptyState ────────────── */
function EmptyState({
  icon: Icon,
  heading,
  sub,
  spinner = false,
}: {
  icon?: React.ComponentType<any>;
  heading: string;
  sub?: string;
  spinner?: boolean;
}) {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-2 p-6">
        {spinner ? (
          <div className="h-8 w-8 mx-auto border-4 border-t-brand rounded-full animate-spin" />
        ) : Icon ? (
          <Icon className="h-6 w-6 text-muted-foreground mx-auto" />
        ) : null}
        <h3 className="font-medium">{heading}</h3>
        {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

/* ────────────── CopyBtn ────────────── */
function CopyBtn({
  copied,
  setCopied,
  title,
  text,
  label = "Copy",
  disabled = false,
}: {
  copied: boolean;
  setCopied: (val: boolean) => void;
  title: string;
  text: string;
  label?: string;
  disabled?: boolean;
}) {
  const { toast } = useToast();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          toast({ title });
          setTimeout(() => setCopied(false), 2000);
        } catch {
          toast({ title: "Copy failed", variant: "destructive" });
        }
      }}
    >
      {copied ? (
        <CheckIcon className="h-4 w-4" />
      ) : (
        <CopyIcon className="h-4 w-4" />
      )}
      <span className="ml-1">{copied ? "Copied" : label}</span>
    </Button>
  );
}
